/**
 * The route-planning module's paths — spec §8.5.
 *
 * `/routes` is what `layout/nav-items.ts` already points its `routes` entry at, gated
 * on `routes:read`, so the prefix is not a choice this phase gets to make; everything
 * else hangs off it.
 *
 * Route paths stay in English while the UI defaults to French, for the reason
 * `nav-items.ts` gives: the address bar is not a translated surface, and a localized
 * URL set would mean two routing tables and a redirect on every language switch.
 *
 * Registration lives in `routes/app-routes.tsx`, which this phase does not own.
 * NOTES-PHASE8-WEB.md §1 carries the patch, and it MUST lazy-load this module's
 * barrel: the adherence report draws two polylines with MapLibre, and a static import
 * would put ~1.45 MB of WebGL renderer back into the entry chunk that the map, trips
 * and zones splits exist to keep it out of.
 */

export const ROUTES_PATH = '/routes'
export const ROUTE_PLAN_NEW_PATH = '/routes/new'
export const ROUTE_PLAN_PATH = '/routes/plans/:planId'
export const ROUTE_PLAN_EDIT_PATH = '/routes/plans/:planId/edit'
export const ROUTE_EXECUTION_PATH = '/routes/executions/:executionId'
export const ROUTE_ADHERENCE_PATH = '/routes/executions/:executionId/report'

export const ROUTE_QUERY_KEYS = {
  status: 'status',
  vehicleId: 'vehicle',
  driverId: 'driver',
  /** `YYYY-MM-DD` day keys in the tenant's zone, inclusive at both ends. */
  from: 'from',
  to: 'to',
  search: 'q',
  /**
   * The punctuality tolerance the report was read at, in seconds. On the URL rather
   * than in component state so that a manager disputing "87 % on time" can be sent a
   * link that reproduces the exact figure — a percentage without its tolerance is a
   * number two people read differently and both are right.
   */
  toleranceS: 'tolerance',
} as const

export function planPath(planId: string): string {
  return `${ROUTES_PATH}/plans/${planId}`
}

export function planEditPath(planId: string): string {
  return `${ROUTES_PATH}/plans/${planId}/edit`
}

export function executionPath(executionId: string): string {
  return `${ROUTES_PATH}/executions/${executionId}`
}

export function adherenceReportPath(executionId: string, toleranceS?: number): string {
  const base = `${ROUTES_PATH}/executions/${executionId}/report`
  return toleranceS === undefined ? base : `${base}?${ROUTE_QUERY_KEYS.toleranceS}=${toleranceS}`
}
